import { Injectable, Injector, NgZone, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  Subscription,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiControllers } from '../constants/api-controllers.constants';
import {
  AUTH_STORAGE_KEY,
  AccessRightDto,
  AuthStoragePayload,
  LoginCompanyDto,
  LoginRequest,
  LoginResponse,
  SelectCompanyResponse,
} from '../models/dtos/auth.dto';
import { ApiService, ApiResponse } from './api.service';
import { EncryptionService } from './encryption.service';
import { MasterDataCacheService } from './master-data-cache.service';
import { ToastService } from '../../shared/toast/toast.service';
import { SessionExpiryService } from '../../shared/session-expiry-modal/session-expiry.service';
import { AccountingCompanyContextService } from './accounting-company-context.service';
import { CurrentCompanyService } from './current-company.service';

export type {
  AccessRightDto,
  AuthStoragePayload,
  LoginCompanyDto,
  LoginRequest,
  LoginResponse,
  SelectCompanyRequest,
  SelectCompanyResponse,
} from '../models/dtos/auth.dto';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiService = inject(ApiService);
  private encryption = inject(EncryptionService);
  private masterDataCache = inject(MasterDataCacheService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private sessionExpiryService = inject(SessionExpiryService);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private sessionWarningTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionCheckInterval: ReturnType<typeof setInterval> | null = null;
  private routerSubscription: Subscription | null = null;
  private handlingSessionExpiry = false;
  private readonly injector = inject(Injector);

  private static readonly SESSION_POLL_MS = 3_000;
  private readonly sessionWarningMs =
    environment.sessionWarningSecondsBeforeExpiry * 1_000;

  private get storage(): Storage | null {
    return isPlatformBrowser(this.platformId) ? localStorage : null;
  }

  getLoginCompanies(): LoginCompanyDto[] {
    return this.getAuthData()?.companies ?? [];
  }

  selectCompany(
    companyId: string,
  ): Observable<ApiResponse<SelectCompanyResponse | null>> {
    return this.apiService.post<SelectCompanyResponse>(
      `${ApiControllers.Auth}/select-company`,
      { companyId: companyId.trim() },
    );
  }

  login(request: LoginRequest): Observable<ApiResponse<LoginResponse | null>> {
    return this.apiService
      .post<LoginResponse>(`${ApiControllers.Auth}/login`, {
        userName: request.userName.trim(),
        password: request.password,
      })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.persistAuthResponse(response.data);
            this.startSessionExpiryWatch();
          }
        }),
        catchError(() =>
          of({
            success: false,
            message: 'Unable to sign in. Please try again.',
            data: null,
          } satisfies ApiResponse<LoginResponse | null>),
        ),
      );
  }

  logout(): void {
    this.logoutWithRevoke();
  }

  logoutWithRevoke(): void {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      this.apiService
        .post<boolean>(`${ApiControllers.Auth}/logout`, { refreshToken })
        .subscribe({ error: () => undefined });
    }
    this.handlingSessionExpiry = false;
    this.finalizeLogout(true);
  }

  /** Exchange refresh token for a new access + refresh token pair. */
  extendSession(): Observable<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken || this.isRefreshTokenExpired()) {
      return of(false);
    }

    return this.apiService
      .post<LoginResponse>(`${ApiControllers.Auth}/refresh`, { refreshToken })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.persistAuthResponse(response.data, true);
            this.sessionExpiryService.close();
            this.handlingSessionExpiry = false;
            this.startSessionExpiryWatch();
          }
        }),
        map((response) => response.success === true && !!response.data),
        catchError(() => of(false)),
      );
  }

  getRefreshToken(): string | null {
    return this.getAuthData()?.refreshToken ?? null;
  }

  canRefreshSession(): boolean {
    return !!this.getRefreshToken() && !this.isRefreshTokenExpired();
  }

  isRefreshTokenExpired(): boolean {
    const expiresAt = this.getAuthData()?.refreshTokenExpiresAt;
    if (!expiresAt) {
      return true;
    }
    const expiryMs = Date.parse(expiresAt);
    return !Number.isFinite(expiryMs) || expiryMs <= Date.now();
  }

  /** Access token expired but refresh may still extend the session. */
  hasExpiredAccessToken(): boolean {
    const token = this.getToken();
    return !!token && this.isTokenExpired(token);
  }

  /** No valid access token and no refresh path left. */
  hasExpiredSession(): boolean {
    if (!this.getAuthData()?.token) {
      return true;
    }
    if (!this.hasExpiredAccessToken()) {
      return false;
    }
    return !this.canRefreshSession();
  }

  /** Force logout when session cannot be extended — clears storage and redirects to login. */
  handleSessionExpired(message = 'Please sign in again.'): void {
    if (this.handlingSessionExpiry) {
      return;
    }
    this.handlingSessionExpiry = true;
    this.finalizeLogout(true, message);
  }

  /** Re-check session on navigation / poll. */
  checkAndHandleExpiredSession(): void {
    if (this.sessionExpiryService.visible()) {
      return;
    }

    if (!this.getAuthData()?.token) {
      return;
    }

    if (!this.hasExpiredAccessToken()) {
      return;
    }

    if (this.canRefreshSession()) {
      this.openSessionWarning(this.getSecondsUntilAccessExpiry());
      return;
    }

    this.handleSessionExpired();
  }

  /** Schedule warning modal before access token expiry. */
  startSessionExpiryWatch(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.stopSessionExpiryWatch();
    this.handlingSessionExpiry = false;

    const token = this.getToken();
    if (!token) {
      return;
    }

    if (this.hasExpiredAccessToken()) {
      this.checkAndHandleExpiredSession();
      return;
    }

    const payload = this.decodeJwtPayload(token);
    const expiryInMs = Number(payload?.exp) * 1000;
    if (!Number.isFinite(expiryInMs)) {
      return;
    }

    const now = Date.now();
    const warningLeadMs = Math.min(
      this.sessionWarningMs,
      Math.max(5_000, expiryInMs - now - 1_000),
    );
    const warnAtMs = expiryInMs - warningLeadMs;
    const warnDelayMs = Math.max(0, warnAtMs - now);
    const expiryDelayMs = Math.max(0, expiryInMs - now + 50);

    this.sessionWarningTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (!this.sessionExpiryService.visible()) {
          this.openSessionWarning(this.getSecondsUntilAccessExpiry());
        }
      });
    }, warnDelayMs);

    this.sessionExpiryTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (this.sessionExpiryService.visible()) {
          this.handleSessionExpired('Your session has ended. Please sign in again.');
        } else if (this.hasExpiredAccessToken()) {
          this.checkAndHandleExpiredSession();
        }
      });
    }, expiryDelayMs);

    this.sessionCheckInterval = setInterval(() => {
      this.ngZone.run(() => this.checkAndHandleExpiredSession());
    }, AuthService.SESSION_POLL_MS);

    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => {
        this.ngZone.run(() => this.checkAndHandleExpiredSession());
      });
  }

  stopSessionExpiryWatch(): void {
    if (this.sessionWarningTimer !== null) {
      clearTimeout(this.sessionWarningTimer);
      this.sessionWarningTimer = null;
    }
    if (this.sessionExpiryTimer !== null) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = null;
    }
    if (this.sessionCheckInterval !== null) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
  }


  getAuthData(): AuthStoragePayload | null {
    const storage = this.storage;
    if (!storage) return null;
    const cipher = storage.getItem(AUTH_STORAGE_KEY);
    return this.encryption.decrypt<AuthStoragePayload>(cipher);
  }

  getToken(): string | null {
    return this.getAuthData()?.token ?? null;
  }

  getUserType(): number | null {
    const user = this.getAuthData()?.user;
    if (!user) {
      return null;
    }

    if (typeof user.userType === 'number' && Number.isFinite(user.userType)) {
      return user.userType;
    }

    const roleParsed = Number(user.userRole);
    return Number.isFinite(roleParsed) ? roleParsed : null;
  }

  /** Current authenticated user id from session (lowercase GUID string), or null. */
  getUserId(): string | null {
    const raw = this.getAuthData()?.user?.userId;
    if (typeof raw !== 'string' || !raw.trim()) {
      return null;
    }
    return raw.trim().toLowerCase();
  }

  isTokenExpired(token: string | null = this.getToken()): boolean {
    if (!token) {
      return true;
    }
    const payload = this.decodeJwtPayload(token);

    if (!payload?.exp) return false;
    const expiryInMs = Number(payload.exp) * 1000;
    return Number.isFinite(expiryInMs) && expiryInMs <= Date.now();
  }

  isLoggedIn(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  arePermissionsReady(): boolean {
    return true;
  }

  getModuleNameByPath(_path: string): string | null {
    return null;
  }

  hasViewAccess(_appModuleName: string): boolean {
    return true;
  }

  hasAddAccess(_appModuleName: string): boolean {
    return true;
  }

  hasEditAccess(_appModuleName: string): boolean {
    return true;
  }

  hasDeleteAccess(_appModuleName: string): boolean {
    return true;
  }

  hasExportAccess(_appModuleName: string): boolean {
    return true;
  }

  hasViewAccessByPath(_path: string): boolean {
    return true;
  }

  hasAddAccessByPath(_path: string): boolean {
    return true;
  }

  hasEditAccessByPath(_path: string): boolean {
    return true;
  }

  hasAccountingAlterAccess(): boolean {
    return true;
  }

  hasVoucherEntryDeleteAccess(): boolean {
    return true;
  }

  hasDeleteAccessByPath(_path: string): boolean {
    return true;
  }

  private openSessionWarning(secondsRemaining: number): void {
    if (!this.canRefreshSession()) {
      this.handleSessionExpired();
      return;
    }
    this.sessionExpiryService.open(Math.max(1, secondsRemaining));
  }

  private getSecondsUntilAccessExpiry(): number {
    const token = this.getToken();
    if (!token) {
      return 0;
    }
    const payload = this.decodeJwtPayload(token);
    const expiryInMs = Number(payload?.exp) * 1000;
    if (!Number.isFinite(expiryInMs)) {
      return 0;
    }
    return Math.max(0, Math.ceil((expiryInMs - Date.now()) / 1_000));
  }

  private persistAuthResponse(data: LoginResponse, preserveCompanies = false): void {
    const existing = preserveCompanies ? this.getAuthData() : null;
    const payload: AuthStoragePayload = {
      token: data.token,
      refreshToken: data.refreshToken,
      refreshTokenExpiresAt: data.refreshTokenExpiresAtUtc,
      accessTokenExpiresAtUtc: data.accessTokenExpiresAtUtc,
      companies: data.companies?.length
        ? data.companies
        : (existing?.companies ?? []),
      user: {
        userId: data.userId,
        userName: data.userName,
        name: data.name || data.userName,
        emailId: data.emailId,
        mobileNo: data.mobileNo,
        userType: data.userType,
      },
    };
    const encrypted = this.encryption.encrypt(payload);
    const storage = this.storage;
    if (storage) {
      storage.setItem(AUTH_STORAGE_KEY, encrypted);
    }
  }

  private finalizeLogout(
    redirectToLogin: boolean,
    sessionExpiredMessage?: string,
  ): void {
    this.stopSessionExpiryWatch();
    this.sessionExpiryService.close();
    this.masterDataCache.clearAll();

    if (isPlatformBrowser(this.platformId)) {
      this.injector.get(AccountingCompanyContextService).shutCompany();
      this.injector.get(CurrentCompanyService).clearSession();
    }

    this.clearBrowserStorage();

    if (!redirectToLogin) {
      return;
    }

    this.ngZone.run(() => {
      if (sessionExpiredMessage) {
        this.toastService.warning('Session Expired', sessionExpiredMessage);
      }
      void this.router.navigate(['/login'], { replaceUrl: true });
    });
  }

  private clearBrowserStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.clear();
  }

  private decodeJwtPayload(token: string): { exp?: number } | null {
    try {
      const payload = token.split('.')[1];

      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        '=',
      );
      return JSON.parse(globalThis.atob(padded));
    } catch {
      return null;
    }
  }
}
